/**
 * A work order as the program handles it (header + items). Totals always come
 * from itemTotals() in workOrder.ts so net + VAT = gross on every line.
 */
import { DomainError } from "./errors";
import type { IntakeExtras } from "./intake";
import { itemTotals, type ItemKind, type ItemTotals, type PriceMode } from "./workOrder";

export type WorkOrderStatus = "open" | "ready" | "closed";

export const WORK_ORDER_STATUSES: readonly WorkOrderStatus[] = ["open", "ready", "closed"];

export interface WorkOrderInput {
  vehicleId: string | null;
  customerId: string | null;
  odometerKm: number | null;
  /** Expected completion date (YYYY-MM-DD), printed on the intake receipt. */
  expectedReadyAt?: string | null;
  customerRequest?: string | null;
  /** The discovered fault ("Feltárt hiba"); shown to the customer with the quote. */
  findings?: string | null;
  notes?: string | null;
  /** "Felelős szerelő": a member of the workshop (owner, manager or mechanic); null: nobody yet. */
  mechanicId?: string | null;
  /** What was left in the car ("intakePhotos" feature); only saved when given (undefined: unchanged). */
  intakeExtras?: IntakeExtras | null;
}

/** Customer approval of a line: none needed, waiting, yes, no (a "no" line counts nowhere). */
export type ItemApproval = "none" | "pending" | "approved" | "declined";

export interface WorkOrderLineInput {
  kind: ItemKind;
  description: string;
  partNumber?: string | null;
  /** Article of the article master; a stock kept article is taken out of stock. */
  articleId?: string | null;
  unit?: string | null;
  quantity: number;
  /** Minor units, net or gross per priceMode. */
  unitPrice: number;
  priceMode: PriceMode;
  vatRate: number;
  /** Needs the customer's yes (quotes feature); defaults to "none". */
  approval?: ItemApproval;
  /** Recommended, not needed for the repair. */
  optional?: boolean;
  /** A part the customer brought ("ügyfél hozta"): shown, never taken from stock. */
  customerSupplied?: boolean;
  /** The mechanic of a labour line; null: the work order's responsible mechanic. Never on other lines. */
  mechanicId?: string | null;
}

export interface WorkOrderLine extends WorkOrderLineInput, ItemTotals {}

/** Checks a line and adds its totals; throws a DomainError on bad input. */
export function buildLine(input: WorkOrderLineInput): WorkOrderLine {
  const description = input.description.trim();
  if (!description) throw new DomainError("ITEM_DESCRIPTION_REQUIRED");
  const totals = itemTotals({ kind: input.kind, quantity: input.quantity, unitPrice: input.unitPrice, priceMode: input.priceMode, vatRate: input.vatRate });
  return {
    ...input,
    description,
    partNumber: input.partNumber?.trim() || null,
    // a part the customer brought is never an article of the stock
    articleId: input.customerSupplied ? null : (input.articleId ?? null),
    unit: input.unit?.trim() || null,
    approval: input.approval ?? "none",
    optional: input.optional ?? false,
    customerSupplied: input.customerSupplied ?? false,
    mechanicId: input.kind === "labour" ? (input.mechanicId ?? null) : null,
    ...totals,
  };
}

/**
 * Odometer plausibility: a reading lower than the last recorded one is usually
 * a typing error (the cluster may have been replaced, so it is a warning only).
 */
/** An odometer reading from the form: a whole, non negative number of km, otherwise null (marked red, button grey). */
export function odometerFromInput(n: number | null): number | null {
  if (n === null || !Number.isFinite(n) || n < 0) return null;
  return Math.round(n);
}

/** Local calendar day (YYYY-MM-DD) of a moment. */
const localDay = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/**
 * Whether a promised ready date has passed. A date without time (the usual case) is late only from the
 * next day: a work order promised for today is still in progress today.
 */
export function isPastReadyDate(expectedReadyAt: string | null | undefined, now: Date = new Date()): boolean {
  if (!expectedReadyAt) return false;
  if (/^\d{4}-\d{2}-\d{2}$/.test(expectedReadyAt)) return expectedReadyAt < localDay(now);
  const t = new Date(expectedReadyAt).getTime();
  return Number.isFinite(t) && t < now.getTime();
}

export function odometerWarning(km: number | null, lastKm: number | null): "lowerThanLast" | null {
  if (km === null || lastKm === null) return null;
  return km < lastKm ? "lowerThanLast" : null;
}

/** Which status changes the buttons offer; closing again or skipping back is not offered. */
export function nextStatuses(status: WorkOrderStatus): WorkOrderStatus[] {
  if (status === "open") return ["ready", "closed"];
  if (status === "ready") return ["open", "closed"];
  return [];
}

/** Lines that count (in totals, on the printed sheet, in stock): everything the customer did not decline. */
export function countedLines<T extends { approval?: ItemApproval }>(lines: readonly T[]): T[] {
  return lines.filter((l) => l.approval !== "declined");
}

/** What a work order waits for, at a glance (list and summary). Texts: `nextStep.<key>`. */
export type NextStepTone = "info" | "warn" | "ok";
export interface NextStep {
  key: "noItems" | "inProgress" | "overdue" | "awaitingApproval" | "readyForHandover" | "transferDue" | "closedPaid" | "closed" | "intakeObjection";
  tone: NextStepTone;
  count?: number;
}

export function workOrderNextStep(
  o: { status: WorkOrderStatus; itemCount?: number; pendingApprovals?: number; paymentMethod?: string | null; paidAt?: string | null; expectedReadyAt?: string | null; openObjections?: number },
  now: Date = new Date(),
): NextStep {
  // the customer said "Nem értek egyet" on the intake link and nobody has dealt with it yet: first of all
  if ((o.openObjections ?? 0) > 0) return { key: "intakeObjection", tone: "warn", count: o.openObjections };
  if (o.status === "closed") {
    if (o.paymentMethod === "transfer" && !o.paidAt) return { key: "transferDue", tone: "warn" };
    return o.paymentMethod ? { key: "closedPaid", tone: "ok" } : { key: "closed", tone: "ok" };
  }
  if ((o.pendingApprovals ?? 0) > 0) return { key: "awaitingApproval", tone: "warn", count: o.pendingApprovals };
  if (o.status === "ready") return { key: "readyForHandover", tone: "info" };
  if ((o.itemCount ?? 0) === 0) return { key: "noItems", tone: "info" };
  if (isPastReadyDate(o.expectedReadyAt, now)) return { key: "overdue", tone: "warn" };
  return { key: "inProgress", tone: "info" };
}
